import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { BUSINESS_ACCOUNTS_SLICE_NAME } from "./businessAccounts.constants"
import {
  deleteBaComment,
  deleteBaCommentAttachment,
  deleteUserBusinessAccount,
  getAdminBusinessAccounts,
  getAlertsChart,
  getBaCommentData,
  getBusinessAccountsExport,
  getBusinessAccountsFilterOptions,
  getStatisticsAdmin,
  saveBaComment
} from "./businessAccounts.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "steps_finished", label: "Steps finished", active: true },
  { field_id: "mode", label: "Mode", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "alerts_count", label: "Alerts sent", active: true },
  { field_id: "paid_alerts_count", label: "Paid Alerts", active: true },
  { field_id: "alerts_in_debt_count", label: "Alerts in Debt", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "business_url", label: "Business URL", active: true },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: true },
  { field_id: "enrollments", label: "Enrollments", active: true },
  { field_id: "created_timestamp", label: "Creation date", active: true },
  { field_id: "user_card", label: "Card", active: true },
  { field_id: "balance_current", label: "Current Balance", active: true },
  { field_id: "updated_stripe_perm", label: "Stripe updated (BH)", active: true },
  { field_id: "connected_payment_processors", label: "Payment processor", active: true },

  { field_id: "enable_auto_refunds", label: "Auto-refund Ethoca&CDRN", active: true },
  { field_id: "auto_refund_amount_other", label: "Auto-refund amount Ethoca&CDRN", active: true },
  { field_id: "auto_refund_amount_rdr", label: "Auto-refund amount RDR", active: true },
  { field_id: "media_source", label: "How did you hear about us", active: true },
  { field_id: "pricing_schema", label: "Pricing scheme", active: true },
  { field_id: "auto_cancel_subscriptions", label: "Auto-cancel subscriptions", active: true },
  { field_id: "auto_cancel_amount", label: "Auto-cancel amount", active: true },
  { field_id: "visits_per_month", label: "Visits per month", active: true },
  { field_id: "visits_updated", label: "Visits updated", active: true }
]

const defaultChartData = {
  last_updated: null,
  chart: {
    labels: [],
    result: {}
  },
  total_value: null
}

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    status: "all",

    steps_finished: "all",
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],

    amount: { min: null, max: null, type: null },
    merchant_descriptor: "all",
    connected_payment_processors: "all",
    alert_id: [],
    user_card: [],

    alerts_count: { min: null, max: null, type: null },
    paid_alerts_count: { min: null, max: null, type: null },
    alerts_in_debt_count: { min: null, max: null, type: null },
    enrollments: [],
    created_timestamp: null,
    enable_auto_refunds: "all",
    auto_refund_amount_other: { min: null, max: null, type: null },
    auto_refund_amount_rdr: { min: null, max: null, type: null },
    balance_current: { min: null, max: null, type: null },
    media_source: "all",
    pricing_schema: "all",
    auto_cancel_subscriptions: "all",
    balance_usage: "all",
    demo_mode: "all",
    auto_cancel_amount: { min: null, max: null, type: null },
    exclude_fields: []
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    country: [],
    bank_name: [],
    connected_payment_processors: [],
    pricing_schema: [],
    auto_cancel_subscriptions: []
  },

  visibleFiltersAdmin: {
    status: true,
    user_email: true,
    business_account: true,
    alerts_count: true
  },

  exportFetching: false,
  exportResult: null,

  chartLoading: false,
  chartData: defaultChartData,

  fetchingStatistics: false,
  statistics: {
    users_counter: 0,
    enrollments_counter: {
      all: null,
      new: null,
      processed: null,
      archive: null,
      need_communication: null
    },
    descriptors_counter: {
      all: null,
      new: null,
      processed: null,
      archive: null,
      need_communication: null
    },
    auto_refund_counter: {
      all: null,
      new: null,
      processed: null,
      archive: null,
      need_communication: null
    },
    requests_counter: 0,
    integrations_counter: 0,
    shopify_invoice_counter: 0
  },

  fetchingBaComment: false,
  baCommentData: null
}

/* ------------- Shared reducer cases ------------- */

// Legacy `businessAccountsFailure` backed several unrelated requests' failure cases and
// always cleared both `fetching` and `fetchingBaComment`, regardless of which one the
// request had actually set on `pending`.
const businessAccountsFailure = (state) => {
  state.fetching = false
  state.fetchingBaComment = false
}

const startFetchingBaComment = (state) => {
  state.fetchingBaComment = true
}

const slice = createSlice({
  name: BUSINESS_ACCOUNTS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `businessAccountsSetProp(key,
    // value)`. RTK actions carry a single payload, so callers pass
    // `businessAccountsSetProp({ key, value })`, matching the `alertsSetProp` slice.
    businessAccountsSetProp(state, action) {
      const { key, value } = action.payload

      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearBusinessAccountsFilters(state) {
      state.filters = initialState.filters
    },
    clearBaCommentsData(state) {
      state.baCommentData = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- statistics ---------------- */
      .addCase(getStatisticsAdmin.pending, (state) => {
        state.fetchingStatistics = true
      })
      .addCase(getStatisticsAdmin.fulfilled, (state, action) => {
        state.fetchingStatistics = false
        state.statistics = action.payload
      })
      .addCase(getStatisticsAdmin.rejected, (state) => {
        state.fetchingStatistics = false
      })

      /* ---------------- filter options ---------------- */
      .addCase(getBusinessAccountsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getBusinessAccountsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getBusinessAccountsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- business accounts list ---------------- */
      .addCase(getAdminBusinessAccounts.pending, (state) => {
        state.fetching = true
      })
      .addCase(getAdminBusinessAccounts.fulfilled, (state, action) => {
        const result = action.payload

        state.fetching = false
        if (result.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getAdminBusinessAccounts.rejected, businessAccountsFailure)

      /* ---------------- delete business account ---------------- */
      .addCase(deleteUserBusinessAccount.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteUserBusinessAccount.fulfilled, (state, action) => {
        const deletedBA = action.payload
        const newResults = state.result.results.filter(
          (el) => el.id !== (deletedBA?.id || deletedBA)
        )

        state.fetching = false
        state.result = {
          count: state.result?.length - 1,
          results: newResults
        }
      })
      .addCase(deleteUserBusinessAccount.rejected, businessAccountsFailure)

      /* ---------------- export ---------------- */
      .addCase(getBusinessAccountsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getBusinessAccountsExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getBusinessAccountsExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- alerts chart ---------------- */
      .addCase(getAlertsChart.pending, (state) => {
        state.chartLoading = true
        state.chartData = {}
      })
      .addCase(getAlertsChart.fulfilled, (state, action) => {
        state.chartLoading = false
        state.chartData = action.payload
      })
      .addCase(getAlertsChart.rejected, (state) => {
        state.chartLoading = false
        state.chartData = defaultChartData
      })

      /* ---------------- BA comments ---------------- */
      .addCase(getBaCommentData.pending, startFetchingBaComment)
      .addCase(getBaCommentData.fulfilled, (state, action) => {
        state.fetchingBaComment = false
        state.baCommentData = action.payload
      })
      .addCase(getBaCommentData.rejected, businessAccountsFailure)

      .addCase(saveBaComment.pending, startFetchingBaComment)
      .addCase(saveBaComment.fulfilled, (state, action) => {
        state.fetchingBaComment = false
        state.baCommentData = action.payload
      })
      .addCase(saveBaComment.rejected, businessAccountsFailure)

      .addCase(deleteBaComment.pending, startFetchingBaComment)
      .addCase(deleteBaComment.fulfilled, (state) => {
        state.fetchingBaComment = false
        state.baCommentData = { ...state.baCommentData, comment_text: null }
      })
      .addCase(deleteBaComment.rejected, businessAccountsFailure)

      // The legacy `deleteBaCommentAttachmentActions.errors` pointed at
      // `Creators.deleteBaCommentAttachmentFailure`, an action the reducer never actually
      // defined — dispatching it would have thrown. Resetting `fetchingBaComment` here (like
      // its sibling comment actions above) is what that case was clearly meant to do.
      .addCase(deleteBaCommentAttachment.pending, startFetchingBaComment)
      .addCase(deleteBaCommentAttachment.fulfilled, (state) => {
        state.fetchingBaComment = false
      })
      .addCase(deleteBaCommentAttachment.rejected, (state) => {
        state.fetchingBaComment = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["business-accounts"]?.data
        if (!preference || preference.version !== BUSINESS_ACCOUNTS_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const businessAccountsActions = slice.actions

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.businessAccounts.filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const BUSINESS_ACCOUNTS_PREFERENCES_VERSION = 1

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "businessAccounts",
    storage,
    // Only the user's own table setup survives a reload — the accounts list, statistics,
    // filter options, export result, chart and comment data are all re-fetched on mount.
    whitelist: ["filters", "filtersLastUpd", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
